export type StudySense = { pos: string; zh: string };
export type StudyContext = { chinese?: string; roles?: string[] };
const key = (text: string) => text.toLowerCase().replace(/[‘’]/g, "'");
// Names are resolved with sentence evidence, not capitalization alone (May/Will/Rose).
const names: Record<string, string> = {
  eric:'埃里克',mia:'米娅',alice:'爱丽丝',tom:'汤姆',lily:'莉莉',jack:'杰克',leo:'利奥',grace:'格蕾丝',
  david:'大卫',henry:'亨利',linda:'琳达',emma:'艾玛',nina:'尼娜',bob:'鲍勃',john:'约翰',ted:'泰德',
  lucy:'露西',rachel:'瑞秋',paul:'保罗',jim:'吉姆',joseph:'约瑟夫',tommy:'汤米',sato:'佐藤',
  taro:'太郎',mennad:'梅纳德',baya:'巴雅',ruriko:'琉璃子',shihai:'世海',yuka:'由香',ziri:'齐里',
  mark:'马克',may:'梅',will:'威尔',rose:'罗斯',bill:'比尔',peter:'彼得',mary:'玛丽',anna:'安娜',sarah:'莎拉',
};
const commonHomonyms: Record<string,string[]> = {
  lily:['百合'],tom:['雄猫'],jack:['千斤顶','插座'],leo:['狮子座'],grace:['优雅','恩典'],
  mark:['标记','分数'],may:['五月','可以','可能'],will:['将','愿意','遗嘱','意志'],rose:['玫瑰','上升','升起'],bill:['账单','帐单','法案'],taro:['芋头'],
};

export function contextualName(words: string[], index: number, sense: StudySense, context: StudyContext, role: string): StudySense | undefined {
  const surface=words[index], base=key(surface).replace(/'s$/, ''), chinese=context.chinese || '';
  const titleCase=/^[A-Z][a-z]+(?:['’]s)?$/.test(surface);
  if(/^[A-Z]{2,}$/.test(surface)) return undefined;
  const explicitName = new RegExp(`\\b${base}\\b`, 'i').test(chinese) || !!names[base] && chinese.includes(names[base]);
  const ordinary = commonHomonyms[base]?.some(meaning=>chinese.includes(meaning));
  const nounRole=/(?:主语|宾语|宾补|呼语|定语)/.test(role);
  if(names[base] && (explicitName || titleCase && nounRole && !ordinary && !/^(?:a|an|the)$/i.test(words[index-1] || ''))) {
    return {pos:'proper',zh:`${names[base]}${/['’]s$/.test(surface)?'的':''}（人名）`};
  }
  if(titleCase && /(?:name is|named|called)\s+$/i.test(words.slice(Math.max(0,index-3),index).join(' ')+' ')) return {pos:'proper',zh:`${surface}（名字）`};
  // The dictionary explicitly identifies this as a person; omit biographies.
  if(titleCase && nounRole && /人名|男子名|女子名|男名|女名/.test(sense.zh)) {
    const short=sense.zh.replace(/\[人名\]/g,'').split(/[,，;；（(]/)[0].trim();
    if(short && short.length<12) return {pos:'proper',zh:`${short}（人名）`};
  }
  return undefined;
}

export function sensePhrases(text: string): string[] {
  return text.replace(/\\n/g,';').replace(/[（(][^()（）]*[）)]/g,'').replace(/\[[^\]]*\]/g,'')
    .replace(/\b(?:abbr|interj|pron|prep|conj|modal|aux|adj|adv|art|num|int|pl|vt|vi|n|v|a)\./gi,'')
    .split(/[,，;；\n]/).map(p=>p.trim()).filter(Boolean);
}

export function conciseStudyMeaning(text: string): string {
  // Do not display English acronym expansions, inflection notes, or entire biographies.
  const parts=sensePhrases(text).filter(p=>!/[A-Za-z]{3}/.test(p) && !/第三人称|常用口语形式|等于|的缩写|电影名|歌曲名/.test(p));
  const short=parts.filter(p=>p.length<=18);
  if(short.length) return short.slice(0,2).join('；').slice(0,24);
  return parts[0] ? `${parts[0].slice(0,17)}…` : '';
}

export function translationSense(sense: StudySense, candidates: StudySense[], chinese: string): StudySense {
  if(!chinese) return sense;
  const matched=candidates.filter(s=>s.pos===sense.pos).flatMap(s=>sensePhrases(s.zh).map(zh=>({pos:s.pos,zh})))
    .filter(s=>s.zh.length>=2 && s.zh.length<=12 && chinese.includes(s.zh)).sort((a,b)=>b.zh.length-a.zh.length);
  return matched[0] || sense;
}

/** High-confidence constructions complement dictionary senses; never rewrite the dictionary. */
export function contextualConstruction(words: string[], index: number, context: StudyContext, current: StudySense): StudySense | undefined {
  const word=key(words[index]), previous=key(words[index-1]||''), next=key(words[index+1]||'');
  const chinese=context.chinese||'', text=words.map(key).join(' ');
  const result=(pos:string,zh:string)=>({pos,zh});
  if(/^(am|is|are|was|were|be|been|being)$/.test(word)) {
    if(/ing$/.test(next) && !/^(interesting|boring|exciting|amazing|tiring|surprising|charming|willing)$/.test(next)) return result('aux','正在');
    if(/^(not|n't)$/.test(next) && /ing$/.test(key(words[index+2]||''))) return result('aux','正在');
    return result('v', '是');
  }
  if(/^(have|has|had)$/.test(word)) {
    if(next==='to') return result('modal','必须');
    if(next==='fun') return result('v','玩得');
    const nextRole=context.roles?.[index+1]||'';
    if(/谓语/.test(nextRole) && /(?:ed|en)$/.test(next) || /^(been|gone|done|seen|eaten|written|read|found|made|taken|had|left|lost|won|bought|told)$/.test(next)) return result('aux','已经');
    if(/^(breakfast|lunch|dinner|meal)$/.test(next)) return result('v','吃');
    return result('v','有');
  }
  if(word==='while' && !/^(a|the|little|short|long)$/.test(previous)) return result('conj',/而|但/.test(chinese)?'而；然而':'当……时');
  if(word==='energy' && (/^(clean|renewable|solar|wind|green|nuclear)$/.test(previous)||/能源/.test(chinese))) return result('n', '能源');
  if(word==='energy' && /能量/.test(chinese)) return result('n', '能量');
  if(word==='clean' && /^(energy|water|air|power)$/.test(next)) return result('adj','清洁的');
  if(word==='model' && /制作|模型/.test(chinese)) return result('n','模型');
  if(word==='room' && /^(music|science|class|reading|meeting|dining|living|bed)$/.test(previous)) return result('n',previous==='music'||previous==='science'?'教室':previous==='meeting'?'会议室':previous==='dining'?'餐厅':previous==='living'?'客厅':'房间');
  if(word==='hall' && previous==='history') return result('n','展厅');
  if(word==='class' && /全班|我们班|我的班|班级/.test(chinese)) return result('n','班级');
  if(word==='school' && /学校|上学/.test(chinese)) return result('n','学校');
  if(/^(mum|mom|mummy)$/.test(word) && !/keep mum/.test(text)) return result('n','妈妈');
  if(/^(dad|daddy)$/.test(word)) return result('n','爸爸');
  if(word==='china' && /中国/.test(chinese)) return result('proper','中国');
  if(word==='japan' && /日本/.test(chinese)) return result('proper','日本');
  if(word==='internet') return result('n','互联网');
  const languages:Record<string,string>={english:'英语',chinese:'汉语',french:'法语',japanese:'日语',russian:'俄语',spanish:'西班牙语',korean:'韩语',german:'德语',mandarin:'普通话',shanghainese:'上海话',nahuatl:'纳瓦特尔语'};
  if(languages[word] && (/(?:speak|speaks|speaking|study|learn|learning|in|teach|teaching)$/.test(previous)||chinese.includes(languages[word]))) return result('n',languages[word]);
  if(word==='may' && (current.pos==='modal' || (context.roles?.[index]||'').includes('谓语'))) return result('modal','可能；可以');
  if(word==='will' && (current.pos==='modal' || (context.roles?.[index]||'').includes('谓语'))) return result('modal','将会');
  if(word==='his') return result('det','他的');
  if(word==='her' && /^(name|book|class|room|family|mother|father|friend)$/.test(next)) return result('det','她的');
  if(word==='her') return result('pron','她');
  if(word==='him') return result('pron','他');
  if(word==='us') return result('pron','我们');
  if(word==='them') return result('pron','他们');
  if(word==='meme' && /梗/.test(chinese)) return result('n','网络梗');
  if(/^\d+$/.test(word)) return result('num',word);
  const contractions:Record<string,string>={"let's":'让我们',"what's":'是什么',"where's":'在哪里',"who's":'谁是',"there's":'有',"there're":'有',"don't":'不',"doesn't":'不',"didn't":'没有',"isn't":'不是',"aren't":'不是',"wasn't":'不是',"weren't":'不是',"can't":'不能',"couldn't":'不能',"won't":'不会',"wouldn't":'不会',"shouldn't":'不应该',"mustn't":'不准',"should've":'本应该',"haven't":'还没有',"hasn't":'还没有',"hadn't":'没有',gonna:'将要'};
  if(contractions[word]) return result('aux',contractions[word]);
  const contraction=word.match(/^(i|you|he|she|it|we|they)'(m|re|s|ve|ll|d)$/);
  if(contraction){
    const subjects:Record<string,string>={i:'我',you:'你',he:'他',she:'她',it:'它',we:'我们',they:'他们'};
    const tail=contraction[2];
    const perfect=/^(been|gone|done|seen|eaten|written|found|made|taken|had|left|lost|won|bought|told)$/.test(next);
    const suffix=tail==='ll'?'将会':tail==='ve'?'已经':tail==='d'?(next==='better'?'最好':perfect?'已经':'会'):tail==='s'&&perfect?'已经': /ing$/.test(next)?'正在':'是';
    return result('aux',subjects[contraction[1]]+suffix);
  }
  return undefined;
}
